import type { Theme } from "@balu/domain";
import type { Snapshot } from "@balu/sync-client";
import { useT } from "../lib/useT.js";
import { useApp } from "../store/app.js";
import { api, getSync } from "../lib/clients.js";
import { canWrite, useMyRole } from "../lib/role.js";
import { unarchiveProject } from "../lib/unarchiveProject.js";
import { syncLabelKey } from "../components/SyncIndicator.js";
import { SyncIndicator } from "../components/SyncIndicator.js";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { IconButton } from "../components/IconButton.js";
import { Icon } from "../components/Icon.js";
import { ProgressRing } from "../components/ProgressRing.js";
import type { TranslationKey } from "../i18n/index.js";

const THEME_CYCLE: Theme[] = ["system", "light", "dark"];
const THEME_ICON: Record<Theme, string> = { system: "monitor", light: "sun", dark: "moon" };

export function Toolbar({ snapshot }: { snapshot: Snapshot }) {
  const { t } = useT();
  const view = useApp((s) => s.view);
  const theme = useApp((s) => s.theme);
  const setTheme = useApp((s) => s.setTheme);
  const setView = useApp((s) => s.setView);
  const setPalette = useApp((s) => s.setPalette);

  let title = "Balu";
  let progress: { value: number; total: number } | null = null;
  let archivedId: string | null = null;
  const writable = canWrite(useMyRole());

  if (view.kind === "list") title = t(`nav.${view.list}` as TranslationKey);
  else if (view.kind === "settings") title = t("settings.title");
  else if (view.kind === "project") {
    const project = snapshot.projects.find((p) => p.id === view.projectId);
    title = project?.name ?? "Balu";
    if (project?.archived_at != null) archivedId = project.id;
    const inProject = snapshot.tasks.filter((tk) => !tk.is_deleted && tk.project_id === view.projectId && tk.parent_task_id == null);
    const done = inProject.filter((tk) => tk.completed_at != null).length;
    if (inProject.length > 0) progress = { value: done, total: inProject.length };
  }

  function cycleTheme() {
    const next = THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length]!;
    setTheme(next);
    void api.patchMe({ theme: next }).catch(() => {});
  }

  return (
    <header
      style={{
        height: 60,
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "0 24px",
        borderBottom: "1px solid var(--border)",
        background: "var(--surface)",
      }}
    >
      <h1 title={title} style={{ margin: 0, fontSize: 24, fontWeight: "var(--weight-semibold)", color: "var(--text-primary)", letterSpacing: "-0.5px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {title}
      </h1>
      {progress && <ProgressRing value={progress.value} total={progress.total} showLabel />}
      {archivedId && <Badge tone="neutral">{t("project.archived")}</Badge>}
      {archivedId && writable && (
        <Button variant="secondary" size="sm" icon="archive-restore" onClick={() => { const sync = getSync(); if (sync) unarchiveProject(sync, archivedId); }}>
          {t("project.unarchive")}
        </Button>
      )}
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={() => setPalette(true)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: 200,
          height: 32,
          padding: "0 12px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-control)",
          color: "var(--text-tertiary)",
          cursor: "pointer",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--text-secondary-size)",
        }}
      >
        <Icon name="search" size={16} color="var(--text-tertiary)" />
        <span style={{ flex: 1, textAlign: "left" }}>{t("toolbar.search")}</span>
        <span style={{ fontSize: 12 }}>⌘K</span>
      </button>
      <SyncIndicator state={snapshot.status} label={t(syncLabelKey(snapshot.status) as TranslationKey)} />
      <IconButton icon={THEME_ICON[theme]} label={t("settings.theme")} onClick={cycleTheme} />
      <IconButton
        icon="settings"
        label={t("settings.title")}
        active={view.kind === "settings"}
        onClick={() => setView({ kind: "settings" })}
      />
    </header>
  );
}
